import { useContext, useMemo } from 'react'

import { type BasketData, BasketContext } from '@/context/basketContext'

interface BasketInfo {
  isBasketInitialised: boolean
  basketData: BasketData
  hasTestKit: boolean
  hasMembership: boolean
  hasDaily30: boolean
  hasBars: boolean
  hasOneTimeProductsOnly: boolean
  isEmpty: boolean
}

/**
 * A custom hook that provides basket information and derived states.
 */
export const useBasketInfo = (): BasketInfo => {
  const context = useContext(BasketContext)
  if (context === undefined) {
    throw new Error('useBasketInfo must be used within a BasketProvider')
  }

  const { basketData, isBasketInitialised } = context

  const hasTestKit = useMemo(
    () => isBasketInitialised && basketData.items?.some((item) => item.priceLookupKey.includes('TEST')),
    [isBasketInitialised, basketData],
  )
  const hasMembership = useMemo(
    () => isBasketInitialised && basketData.items?.some((item) => item.priceLookupKey.includes('ZOE_PLUS')),
    [isBasketInitialised, basketData],
  )
  const hasDaily30 = useMemo(
    () => (isBasketInitialised && basketData.items?.some((item) => item.priceLookupKey.includes('DAILY30'))) ?? false,
    [isBasketInitialised, basketData],
  )
  const hasBars = useMemo(
    () => (isBasketInitialised && basketData.items?.some((item) => item.priceLookupKey.includes('BAR'))) ?? false,
    [isBasketInitialised, basketData],
  )
  const hasOneTimeProductsOnly = useMemo(
    () =>
      basketData.items?.every(
        (item) => item.priceLookupKey.includes('ONE_TIME') || item.priceLookupKey.includes('BARS_WELCOME'),
      ),
    [hasBars, basketData],
  )
  const isEmpty = useMemo(() => !isBasketInitialised || !basketData.items?.length, [isBasketInitialised, basketData])

  return {
    isBasketInitialised,
    basketData,
    hasTestKit,
    hasMembership,
    hasDaily30,
    hasBars,
    hasOneTimeProductsOnly,
    isEmpty,
  }
}
